import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  NotificationActionPreset,
  notificationAction,
  NotificationService,
  NotificationType,
} from '../components/shared/notification';

/** The copy and behaviour chosen for one failure class. */
export interface ApiErrorCopy {
  type: NotificationType;
  /** Bold lead-in naming the failure class, e.g. "Permission denied." */
  title: string;
  /** Body copy. Curated, except for the field-level 400/422 case below. */
  message: string;
  /** `0` keeps the toast up until dismissed — used where the user must act. */
  duration: number;
  /** Offer a page refresh, for the cases whose copy asks for one. */
  offerRefresh: boolean;
  /**
   * True when {@link message} is a field-level validation sentence the API
   * returned rather than one of the constants below. Callers with their own
   * screen-specific wording defer to it, since it is the more specific of the
   * two.
   */
  fromServer?: boolean;
}

/**
 * One sentence per failure class — the agreed portal-wide error copy.
 *
 * Each says what happened and what to do next, in that order, and never names
 * anything internal. Selected by status in {@link describeApiFailure}.
 */
export const API_ERROR_COPY = {
  /** Status 0, and any request made while the browser reports no network. */
  offline: 'You appear to be offline. Please check your internet connection and try again.',
  /**
   * Status 0 while the browser still reports a connection.
   *
   * The request produced no response we are allowed to read: DNS or TLS
   * failure, a dropped socket, an aborted request — or, commonly, an error
   * response the browser refused to hand over because it carried no CORS
   * headers. A gateway 504 whose error page omits those headers arrives here
   * looking like nothing at all, which is why this copy does not name a cause.
   */
  unreachable: 'We couldn’t reach the server. Please try again, or contact support if this continues.',
  /** 500, 502, 503. */
  server: 'Our servers are having trouble right now. Please try again shortly.',
  /**
   * 504, 408, client-side timeouts from the rxjs `timeout()` operator, and a
   * status-0 failure that took long enough to have been a gateway giving up.
   */
  timeout: 'This is taking longer than expected. Please try again.',
  /** 401. */
  session: 'Your session has expired. Please log in again.',
  /** 403. */
  forbidden: 'You don’t have permission to perform this action.',
  /** 404. */
  notFound: 'We couldn’t find what you were looking for. Please contact support if this continues.',
  /** 400, 409, 422 — used when the API sent no usable field-level message. */
  validation: 'Please check your input and try again.',
  /** 429. */
  rateLimited: 'You’re doing that too fast. Please wait a moment and try again.',
  /**
   * Auth screens only, via {@link friendlyAuthErrorMessage}. The HTTP path has
   * no equivalent — an unrecognised failure there shows nothing — but a sign-in
   * form that rejected the user has to say something.
   */
  unknown: 'Something unexpected happened. Please try again or contact support if it persists.',
} as const;

/**
 * How long a status-0 failure must have run before it is read as a timeout
 * rather than as a connection that was never there.
 *
 * Gateways give up slowly — 29s on API Gateway, 60s by default on an ALB — and
 * their timeout pages routinely omit CORS headers, so the browser withholds the
 * response and the 504 arrives as a bare status 0. A connection that genuinely
 * cannot be made fails far below this: a refused TCP connect is immediate and
 * even a slow DNS failure is seconds. Nothing legitimate sits near the line.
 */
const GATEWAY_TIMEOUT_FLOOR_MS = 15000;

/**
 * Elapsed time of a failed request, keyed by the thrown error itself.
 *
 * A WeakMap rather than a property on the error so nothing is bolted onto an
 * `HttpErrorResponse` that callers also receive, and so the entry disappears
 * with the error it describes.
 */
const elapsedByError = new WeakMap<object, number>();

/**
 * Records how long a failed request ran. Called by the HTTP interceptor, which
 * is the only place that knows when the request started.
 *
 * Without it a status-0 failure is unclassifiable and gets the conservative
 * {@link API_ERROR_COPY.unreachable} wording; with it, a slow one is correctly
 * reported as the timeout it was.
 */
export function recordRequestDuration(error: unknown, elapsedMs: number): void {
  if (error && typeof error === 'object' && Number.isFinite(elapsedMs)) {
    elapsedByError.set(error, elapsedMs);
  }
}

function elapsedOf(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  return elapsedByError.get(error) ?? null;
}

/**
 * What the user is told when a request fails, chosen by failure class.
 *
 * Deliberately the *only* source of failure wording in the portal. Backend
 * messages name tables, columns, constraints, row keys and internal sequence
 * numbers — details that mean nothing to a business user, read as a defect when
 * they do surface, and in a portal handling patient data are exactly the kind of
 * internals that should not reach a screen. Every entry in {@link API_ERROR_COPY}
 * says what happened and what to do about it instead.
 *
 * The single exception is a 400/422 carrying a field-level validation message
 * ("Start date must be before end date"), which is more useful than any generic
 * sentence we could write. Those are admitted only through
 * {@link fieldLevelMessage}, which rejects anything that reads like internals.
 *
 * Returns `null` when the failure cannot be placed — no status to read and no
 * recognised client-side shape. There is no generic "something went wrong" copy
 * behind that: callers treat `null` as "say nothing".
 *
 * Nothing is lost for diagnosis: the raw error still reaches every `catchError`
 * untouched and is still logged to the console for support.
 */
export function describeApiFailure(error: unknown): ApiErrorCopy | null {
  const status = statusOf(error);

  // Browser reports no network at all — the data on screen is still the last
  // good load, so this is a warning rather than a hard failure. No refresh is
  // offered: with no connection it can only fail.
  //
  // Guarded on there being no status to read. `navigator.onLine` goes stale
  // often enough to be untrustworthy on its own, and a response carrying a real
  // status is proof the server was reached — telling someone holding a 504 that
  // they are offline sends them to check a connection that demonstrably works.
  // The status is the better evidence, so it wins.
  if (isOffline() && status <= 0) {
    return {
      type: NotificationType.Warning,
      title: 'No internet connection.',
      message: API_ERROR_COPY.offline,
      duration: 10000,
      offerRefresh: false,
    };
  }

  // Client-side timeout from `timeout()` — an rxjs TimeoutError, not an
  // HttpErrorResponse, so it has no status to switch on.
  if (isClientTimeout(error)) {
    return {
      type: NotificationType.Warning,
      title: 'Taking longer than expected.',
      message: API_ERROR_COPY.timeout,
      duration: 10000,
      offerRefresh: true,
    };
  }

  // A failed token refresh arrives as a Cognito exception rather than an
  // HttpErrorResponse, so it carries no status and would otherwise fall out of
  // the switch unclassified — leaving the one failure the user *must* act on as
  // the only one reported on no screen at all.
  if (isSessionEnded(error)) {
    return {
      type: NotificationType.Error,
      title: 'Session expired.',
      message: API_ERROR_COPY.session,
      duration: 0,
      offerRefresh: false,
    };
  }

  // A success status that still arrived as an error. Angular reports that in
  // one situation only: the body could not be parsed as the JSON it was asked
  // for — an endpoint answering 200 with an error string, an HTML error page or
  // an empty body. The status is no use for classifying it, since 200 matches no
  // case below and would fall straight through to silence, so it is recognised
  // by its shape instead. From the user's side the request simply failed, and it
  // failed on the server's side of the wire.
  if (isUnparseableSuccess(error)) {
    return {
      type: NotificationType.Error,
      title: 'Server error.',
      message: API_ERROR_COPY.server,
      duration: 10000,
      offerRefresh: true,
    };
  }

  switch (status) {
    // No readable response: DNS, CORS, TLS, a dropped socket, or an error
    // response the browser withheld for want of CORS headers.
    //
    // Note this is *not* the offline case — that returned above, so reaching
    // here means the browser believes it has a connection, and saying otherwise
    // would contradict the check just made. What is left is told apart by how
    // long it took, which is the only evidence a blocked response leaves behind.
    case 0: {
      const elapsed = elapsedOf(error);

      // Slow: a gateway timed out and its error page reached us stripped of the
      // headers needed to read the 504 off it. The user waited, so the wait is
      // what to name.
      if (elapsed !== null && elapsed >= GATEWAY_TIMEOUT_FLOOR_MS) {
        return {
          type: NotificationType.Warning,
          title: 'Taking longer than expected.',
          message: API_ERROR_COPY.timeout,
          duration: 10000,
          offerRefresh: true,
        };
      }

      // Fast, or unmeasured: the server was not reached at all.
      return {
        type: NotificationType.Error,
        title: 'Connection problem.',
        message: API_ERROR_COPY.unreachable,
        duration: 10000,
        offerRefresh: true,
      };
    }

    // Normally intercepted for a token refresh; this is the refresh failing.
    case 401:
      return {
        type: NotificationType.Error,
        title: 'Session expired.',
        message: API_ERROR_COPY.session,
        duration: 0,
        offerRefresh: false,
      };

    case 403:
      return {
        type: NotificationType.Warning,
        title: 'Permission denied.',
        message: API_ERROR_COPY.forbidden,
        duration: 10000,
        offerRefresh: false,
      };

    case 404:
      return {
        type: NotificationType.Warning,
        title: 'Not found.',
        message: API_ERROR_COPY.notFound,
        duration: 8000,
        offerRefresh: false,
      };

    // The request was understood but rejected — a validation failure, a stale
    // record, or an unsupported combination. "Unable to load data" would be
    // wrong here: it is usually the user's own input that needs revisiting.
    // 409 rides along: a conflict is likewise something the user resolves by
    // reviewing what they submitted.
    case 400:
    case 409:
    case 422: {
      const fieldMessage = fieldLevelMessage(error);
      return {
        type: NotificationType.Warning,
        title: 'Please check your input.',
        message: fieldMessage ?? API_ERROR_COPY.validation,
        duration: 10000,
        offerRefresh: false,
        fromServer: fieldMessage !== null,
      };
    }

    case 408:
    case 504:
      return {
        type: NotificationType.Warning,
        title: 'Taking longer than expected.',
        message: API_ERROR_COPY.timeout,
        duration: 10000,
        offerRefresh: true,
      };

    // Retrying immediately is the one thing that makes this worse, so no
    // refresh action here either.
    case 429:
      return {
        type: NotificationType.Warning,
        title: 'Too many requests.',
        message: API_ERROR_COPY.rateLimited,
        duration: 10000,
        offerRefresh: false,
      };

    case 500:
    case 502:
    case 503:
      return {
        type: NotificationType.Error,
        title: 'Server error.',
        message: API_ERROR_COPY.server,
        duration: 10000,
        offerRefresh: true,
      };
  }

  // A status the cases above don't name — 405, 413, 415, 501, 520 and the rest.
  // These are still classified, by range: a 5xx is a failure on our side and a
  // 4xx is something about the request, which is as much as the specific codes
  // above tell the user anyway. Reusing that copy is accurate; it is only the
  // generic "something went wrong" sentence that isn't, which is why there
  // isn't one.
  if (status >= 500 && status <= 599) {
    return {
      type: NotificationType.Error,
      title: 'Server error.',
      message: API_ERROR_COPY.server,
      duration: 10000,
      offerRefresh: true,
    };
  }

  if (status >= 400 && status <= 499) {
    return {
      type: NotificationType.Warning,
      title: 'Please check your input.',
      message: API_ERROR_COPY.validation,
      duration: 10000,
      offerRefresh: false,
    };
  }

  // No status anywhere, and no recognised client-side shape. Anything said here
  // would name neither what happened nor what to do next, so nothing is said.
  return null;
}

/**
 * The user-facing sentence for a failed call.
 *
 * Use it wherever a component renders its own failure state (an inline widget
 * message, a dialog, a snackbar) so the portal says the same business-friendly
 * thing everywhere instead of surfacing whatever the API happened to return:
 *
 * ```ts
 * error: (err) => {
 *   console.error('Save template failed', err);   // full detail, for support
 *   this.snack(friendlyApiErrorMessage(err, 'We couldn’t save your template.'));
 * },
 * ```
 *
 * Pass `fallback` when the caller has wording that suits its own screen better
 * than the generic status copy. It is honoured except where it would misdirect
 * the user: the cases whose cause the caller cannot know — offline, session
 * expiry, no permission and rate limiting, where "We couldn't save your
 * template." would send them looking in the wrong place — and a field-level
 * validation message, which is more specific than any screen-level sentence.
 */
export function friendlyApiErrorMessage(error: unknown, fallback?: string): string {
  const copy = describeApiFailure(error);
  if (!copy) {
    return fallback ?? '';
  }
  if (!fallback) {
    return copy.message;
  }

  const status = statusOf(error);
  if (
    (isOffline() && status <= 0) ||
    copy.fromServer ||
    status === 0 ||
    status === 401 ||
    status === 403 ||
    status === 429
  ) {
    return copy.message;
  }

  return fallback;
}

/**
 * The same treatment for a Cognito / Amplify auth failure, which arrives as a
 * named exception rather than an HTTP status.
 *
 * The sign-in screens switch on `err.name` for the codes they have specific
 * wording for; this is what the remaining branches use instead of `err.message`.
 * Amplify's own text is not shown: it ranges from usable ("Invalid verification
 * code provided") to "1 validation error detected: Value at 'password' failed to
 * satisfy constraint: Member must satisfy regular expression pattern: …", and a
 * screen cannot tell which it is about to render.
 *
 * `fallback` is the caller's screen-specific sentence, used for anything not
 * recognised below. Without one, the generic unknown-failure copy is returned.
 */
export function friendlyAuthErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof HttpErrorResponse) {
    return friendlyApiErrorMessage(error, fallback);
  }
  if (isOffline()) {
    return API_ERROR_COPY.offline;
  }

  switch ((error as { name?: string } | null)?.name) {
    case 'NetworkError':
    case 'NetworkErrorException':
      return API_ERROR_COPY.offline;

    // Cognito throttling — the same story as a 429.
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return API_ERROR_COPY.rateLimited;

    case 'NotAuthorizedException':
      return 'Your session or credentials are no longer valid. Please log in again.';

    case 'CodeMismatchException':
      return 'That code is not correct. Please check it and try again.';

    case 'ExpiredCodeException':
      return 'That code has expired. Please request a new one.';

    case 'UserNotFoundException':
    case 'ResourceNotFoundException':
      return 'We couldn’t find an account for those details. Please check them and try again.';

    case 'UserNotConfirmedException':
      return 'Your account isn’t confirmed yet. Please contact support to finish setting it up.';

    // Amplify's own text here is the raw Cognito password-policy regex.
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return 'Please check your input and try again. Passwords need at least 8 characters with upper and lower case letters, a number and a symbol.';

    case 'InternalErrorException':
      return API_ERROR_COPY.server;

    default:
      return fallback ?? API_ERROR_COPY.unknown;
  }
}

/**
 * The HTTP status behind a thrown value, or `-1` when it carries none.
 *
 * Not every failure arrives as an `HttpErrorResponse`. A service that catches
 * and rethrows its own shape, an AWS SDK client and a hand-built rejection all
 * keep the status somewhere other than `.status`, and read as status-less if
 * only that one property is checked. Since an unplaceable failure is now
 * reported as nothing at all, missing a status here is the difference between
 * a real 403 being explained and it passing in silence.
 *
 * Only real HTTP codes count, so an envelope's `{ status: 'error' }` — a label,
 * not a code — cannot be mistaken for one.
 */
function statusOf(error: unknown): number {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }
  if (!error || typeof error !== 'object') {
    return -1;
  }

  const record = error as Record<string, any>;

  for (const candidate of [
    record['status'],
    record['statusCode'],
    record['httpStatusCode'],
    // Nested under whatever wrapper rethrew it.
    record['response']?.['status'],
    record['response']?.['statusCode'],
    record['error']?.['status'],
    record['originalError']?.['status'],
    record['cause']?.['status'],
    // Amplify / AWS SDK v3 put it here.
    record['$metadata']?.['httpStatusCode'],
  ]) {
    const status = asHttpStatus(candidate);
    if (status !== -1) {
      return status;
    }
  }

  return -1;
}

/** A candidate counts only as a number in the HTTP range, or `0`. */
function asHttpStatus(value: unknown): number {
  const numeric = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof numeric !== 'number' || !Number.isInteger(numeric)) {
    return -1;
  }
  // `0` is the "request never produced a readable response" case.
  if (numeric === 0) {
    return 0;
  }
  return numeric >= 100 && numeric <= 599 ? numeric : -1;
}

/**
 * Cognito / Amplify names that mean the session itself is finished, as opposed
 * to one request being refused. These reach us from a failed token refresh.
 */
const SESSION_ENDED_ERRORS = new Set([
  'NotAuthorizedException',
  'UserUnAuthenticatedException',
  'UnauthenticatedError',
  'TokenExpiredException',
  'SessionExpiredException',
  'RefreshTokenExpiredException',
]);

function isSessionEnded(error: unknown): boolean {
  // The interceptor signals "no session to attach" by throwing this bare
  // string. It carries no status and no name, so without recognising it the one
  // failure the user has to act on would be the one reported on no screen.
  if (typeof error === 'string') {
    return error === 'Unauthorized';
  }

  const name = (error as { name?: string } | null)?.name;
  return name !== undefined && SESSION_ENDED_ERRORS.has(name);
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Whether a response came back with a success status and still failed.
 *
 * Angular raises an `HttpErrorResponse` on a 2xx only when the body could not
 * be parsed, which is why the status alone cannot classify it.
 */
function isUnparseableSuccess(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status >= 200 && error.status < 300;
}

/** The shape rxjs `timeout()` throws — no status, identified by name. */
function isClientTimeout(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'TimeoutError';
}

/**
 * A field-level validation sentence from a 400/422 response body, or `null`.
 *
 * The one place server text is allowed through, because "Start date must be
 * before end date" tells the user something "Please check your input" cannot.
 * Everything else the backend might put in the same field — SQL, ORA codes,
 * stack traces, constraint and column names, serialised objects — is rejected by
 * {@link looksInternal}, falling back to the curated copy.
 */
function fieldLevelMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  const body = parseBody(error.error);
  if (!body) {
    return null;
  }

  for (const candidate of candidateMessages(body)) {
    const clean = candidate.trim().replace(/\s+/g, ' ');
    if (clean && !looksInternal(clean)) {
      return /[.!?]$/.test(clean) ? clean : `${clean}.`;
    }
  }

  return null;
}

/** Response bodies arrive parsed, as a JSON string, or as plain text. */
function parseBody(raw: unknown): unknown {
  if (typeof raw !== 'string') {
    return raw ?? null;
  }

  const text = raw.trim();
  if (!text) {
    return null;
  }
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
  return text;
}

/**
 * Every place a validation message plausibly sits, most-specific first, across
 * the response shapes this portal's endpoints produce: a bare string, a
 * `{ message }` / `{ error }` envelope, a `{ errors: [...] }` list of field
 * failures, a `{ errors: { field: [...] } }` map, or FastAPI's
 * `{ detail: [{ loc, msg }] }`.
 */
function candidateMessages(body: unknown): string[] {
  if (typeof body === 'string') {
    return [body];
  }
  if (Array.isArray(body)) {
    return body.flatMap((entry) => candidateMessages(entry));
  }
  if (typeof body !== 'object' || body === null) {
    return [];
  }

  const record = body as Record<string, unknown>;
  const out: string[] = [];

  for (const key of ['errors', 'detail', 'details', 'validationErrors', 'fieldErrors']) {
    const value = record[key];
    if (typeof value === 'string') {
      out.push(value);
    } else if (Array.isArray(value) || (value && typeof value === 'object')) {
      out.push(...fromCollection(value));
    }
  }

  for (const key of ['message', 'error_message', 'errorMessage', 'error', 'title']) {
    const value = record[key];
    if (typeof value === 'string') {
      out.push(value);
    } else if (value && typeof value === 'object') {
      out.push(...candidateMessages(value));
    }
  }

  return out;
}

/** Field-failure lists and `{ field: [messages] }` maps, flattened. */
function fromCollection(value: unknown): string[] {
  const entries = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);

  return entries.flatMap((entry) => {
    if (typeof entry === 'string') {
      return [entry];
    }
    if (Array.isArray(entry)) {
      return fromCollection(entry);
    }
    if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      // `msg` is FastAPI's spelling; the rest are the common hand-rolled ones.
      for (const key of ['message', 'msg', 'error', 'description']) {
        if (typeof record[key] === 'string') {
          return [record[key] as string];
        }
      }
    }
    return [];
  });
}

/**
 * Whether a candidate reads as machine detail rather than as something we would
 * have written ourselves. Errs towards rejection: the cost of a false reject is
 * the generic sentence, the cost of a false accept is a stack trace on screen.
 */
function looksInternal(text: string): boolean {
  // Long enough to be a log line, or too terse to be a sentence.
  if (text.length > 180 || text.split(' ').length < 3) {
    return true;
  }

  // An unbroken 40-character run is an id, a hash or a qualified identifier
  // rather than prose.
  if (/\S{40,}/.test(text)) {
    return true;
  }

  // Serialised structures, code and paths.
  if (/[{}<>\\|]|\[\s*['"]|;\s|::|=>|\bat\s+\w+\.\w+|https?:\/\//i.test(text)) {
    return true;
  }

  // SQL is matched on shape, not on its keywords in isolation: "Please select
  // at least one brand" is precisely the copy this function exists to admit,
  // and a bare `select` needle would have thrown it away.
  if (
    /\bselect\b[\s\S]*\bfrom\b/i.test(text) ||
    /\binsert\s+into\b/i.test(text) ||
    /\bupdate\b[\s\S]*\bset\b/i.test(text) ||
    /\bdelete\s+from\b/i.test(text) ||
    /\bwhere\s+\w+\s*[=<>]/i.test(text) ||
    /\b(inner|outer|left|right|cross)\s+join\b/i.test(text)
  ) {
    return true;
  }

  const lower = text.toLowerCase();
  return [
    'ora-',
    'sql',
    'constraint',
    'foreign key',
    'primary key',
    'column',
    'table',
    'schema',
    'database',
    'traceback',
    'exception',
    'stack',
    'null pointer',
    'undefined is not',
    'internal server',
    'http failure',
    'errno',
    'lambda',
    'timeout expired',
    'connection refused',
  ].some((needle) => lower.includes(needle));
}

/**
 * Turns a failed HTTP call into a user-facing notification.
 *
 * Kept out of the interceptor so the wording and severity of API failures is
 * one readable table rather than branches inside the request pipeline.
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorNotifierService {
  private readonly notify = inject(NotificationService);

  /** Timestamp of the last failure, for {@link hasRecentFailure}. */
  private lastFailureAt = 0;

  /**
   * Whether an API call failed in the last `withinMs`. Widgets set their empty
   * state in error callbacks as well as on genuinely empty results, so the
   * no-data notifier uses this to stay quiet when the real story is already on
   * screen as a failure.
   */
  hasRecentFailure(withinMs: number): boolean {
    return this.lastFailureAt > 0 && Date.now() - this.lastFailureAt < withinMs;
  }

  /** Records a failed request even when its user-facing toast is suppressed. */
  recordFailure(): void {
    this.lastFailureAt = Date.now();
  }

  /** Raises the toast for a failed request. Safe to call with any thrown value. */
  notifyFailure(error: unknown): void {
    const copy = describeApiFailure(error);
    if (!copy) {
      // Left unstamped deliberately: lastFailureAt only exists to stop the
      // no-data notice doubling up on a failure toast, and with no toast shown
      // suppressing that notice too would leave the user with silence.
      return;
    }

    this.lastFailureAt = Date.now();

    this.notify.show(copy.type, {
      title: copy.title,
      message: copy.message,
      duration: copy.duration,
      // A dashboard fires many parallel requests; when the backend goes down
      // they all fail at once. Keying by failure class collapses that burst
      // into a single toast instead of one per widget. Field-level validation
      // messages key on their own text instead: two rejected fields are two
      // different things to tell the user.
      key: copy.fromServer ? `api-error-${copy.message}` : `api-error-${statusOf(error)}`,
      action: copy.offerRefresh
        ? notificationAction(NotificationActionPreset.Refresh, () => this.reload())
        : undefined,
    });
  }

  /**
   * The curated sentence this service would show for `error`. Exposed for
   * components that render their own failure state instead of a toast; see the
   * standalone {@link friendlyApiErrorMessage}.
   */
  messageFor(error: unknown): string {
    return describeApiFailure(error)?.message ?? '';
  }

  private reload(): void {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }
}
